import { ChangeEvent, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Download,
  FileDown,
  FileText,
  Loader2,
  Lock,
  Share2,
  Trash2,
  Upload,
} from "lucide-react";

import { ApiError } from "@/shared/api";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { EmptyState } from "@/shared/ui/QueryState";
import { StatusBadge } from "@/shared/ui/StatusBadge";

import { kbFailureText, knowledgeService, type KnowledgeDocument } from "@/entities/knowledge";
import { downloadPersonalDocTemplate, PERSONAL_DOC_TEMPLATES } from "../lib/personalDocTemplates";
import {
  PERSONAL_DOC_ACCEPT,
  PERSONAL_DOC_MAX_COUNT,
  partitionPersonalDocUploads,
  personalDocCountWarning,
} from "../lib/personalDocsUploadLimits";
import { usePersona } from "@/shared/brand/persona";

// react-query key。不导出(react-refresh 只允许组件文件导出组件)——目前只有本文件内部用到,
// 真要跨文件复用再挪进 lib/。
const PERSONAL_DOCS_QUERY_KEY = ["personal-knowledge-documents"];

// 三态展示(2026-09-07):个人文档不显示审核轴(见 knowledgeService.ts 头注),
// 所以复用 ingest 状态值即可,不用像租户列表那样再叠一层 reviewStatus/visibility 徽章。
const STATUS_TYPE: Record<string, "success" | "warning" | "error"> = {
  done: "success",
  processing: "warning",
  pending: "warning",
  failed: "error",
};
const STATUS_LABEL: Record<string, string> = {
  done: "可用",
  processing: "处理中",
  pending: "处理中",
  failed: "失败",
};

/**
 * 个人知识库(2026-09-07,一期,老板拍板)。
 *
 * 员工在知识库页目前只读——上传/删除都锁在 MANAGER+(见 `kbUploadGate`)。这个面板是员工在
 * 这个页面**唯一能写**的地方:自己上传的私人文档,只有自己看得见,不进 `KbDocReviewQueue`
 * 待审队列,也不需要"还没人批"那句话——个人文档从设计上就不会被搬进公开检索面。
 *
 * 想让内容对全租户可用,走「共享到租户库」——那才是它第一次真正接触治理链路的地方。
 */
export function PersonalDocsPanel() {
  const persona = usePersona();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 串行上传进度,同 KnowledgeBasePage 那份多文件上传的姿势;个人文档没有 tag/所属产品要选,
  // 不需要弹一个 Dialog 收集元数据——选完文件直接传。
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const isUploading = uploadProgress !== null;

  const {
    data: documents = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: PERSONAL_DOCS_QUERY_KEY,
    queryFn: knowledgeService.listPersonalDocuments,
  });

  const deleteMutation = useMutationWithToast({
    mutationFn: (id: number) => knowledgeService.deletePersonalDocument(id),
    successToast: "文档已删除",
    errorToast: "删除失败",
    invalidateKeys: [PERSONAL_DOCS_QUERY_KEY],
  });

  const downloadMutation = useMutationWithToast({
    mutationFn: ({ id, filename }: { id: number; filename: string }) =>
      knowledgeService.downloadPersonalDocument(id, filename),
    errorToast: "下载失败",
  });

  const promoteMutation = useMutationWithToast<KnowledgeDocument, number>({
    mutationFn: (id) => knowledgeService.promotePersonalDocument(id),
    // 2026-09-16:转正默认落**本部门**而不是一步跨到全公司 —— 结果直接念给用户听,
    // 别让他点完还要去共享库列表里翻才知道给了谁看。范围取后端返回值,不在前端另推一遍。
    successToast: (doc) =>
      doc?.visibleScope === "DEPT"
        ? {
            title: "已共享到本部门,等待审核",
            description: "同部门同事可见;要给全公司看,请管理员在知识库里调整可见范围",
          }
        : { title: "已提交共享,等待审核", description: "过审后全公司同事可见" },
    errorToast: "共享失败(未生效,可重试)",
    // ★不走 invalidateKeys:后端 promote 成功后这份文档已经 scope=TENANT、从 personal 列表里
    // 摘掉了,下面 onSuccess 已经把它从本地缓存里过滤掉——这就是「最新状态」。如果这里再顺手
    // invalidate 触发一次后台 refetch,反而会跟这次同步过滤赛跑:谁的响应后到谁覆盖谁,
    // 在只返回静态列表的测试环境里会被那次 refetch 把刚移除的文档重新写回来。
    onSuccess: (_doc, id) => {
      queryClient.setQueryData<KnowledgeDocument[]>(PERSONAL_DOCS_QUERY_KEY, (prev) =>
        (prev ?? []).filter((d) => d.id !== id)
      );
    },
  });

  const runSequentialUpload = async (files: File[]) => {
    setUploadProgress({ done: 0, total: files.length });
    let successCount = 0;
    const failures: string[] = [];
    const failReasons = new Set<string>();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ done: i, total: files.length });
      try {
        await knowledgeService.uploadPersonalDocument(file);
        successCount++;
      } catch (e) {
        failures.push(file.name);
        // 超限(20MB/份 或 20 份总量)后端返人话 message,直接给用户看,不用前端再翻译一遍。
        if (e instanceof ApiError) failReasons.add(e.message);
      }
    }

    setUploadProgress(null);
    queryClient.invalidateQueries({ queryKey: PERSONAL_DOCS_QUERY_KEY });

    if (failures.length === 0) {
      toast({
        title: "上传成功",
        description: `成功上传 ${successCount}/${files.length} 个文件`,
      });
      return;
    }
    const preview = failures.slice(0, 3).join("、");
    const more = failures.length > 3 ? ` 等 ${failures.length} 个` : "";
    const reasonText =
      failReasons.size > 0 ? `;原因:${Array.from(failReasons).slice(0, 2).join("；")}` : "";
    toast({
      title: "上传失败",
      description: `成功 ${successCount}/${files.length},失败:${preview}${more}${reasonText}`,
      variant: "destructive",
    });
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // 清空 input value,让再次选择"同一批"文件也能触发 onChange(同 KnowledgeBasePage 的姿势)。
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;

    const { accepted, oversized, overLimit } = partitionPersonalDocUploads(files, documents.length);
    if (oversized.length > 0) {
      toast({
        title: "部分文件超过 20MB,未加入本次上传",
        description: oversized.map((f) => f.name).join("、"),
        variant: "destructive",
      });
    }
    if (overLimit.length > 0) {
      toast({
        title: `个人文档最多 ${PERSONAL_DOC_MAX_COUNT} 份`,
        description: `以下文件本次未上传(超出份数上限):${overLimit.map((f) => f.name).join("、")}`,
        variant: "destructive",
      });
    }
    if (accepted.length === 0) return;
    void runSequentialUpload(accepted);
  };

  const atCap = documents.length >= PERSONAL_DOC_MAX_COUNT;
  const countWarning = personalDocCountWarning(documents.length);

  const uploadButton = (
    <Button size="sm" disabled={isUploading || atCap} onClick={() => fileInputRef.current?.click()}>
      {isUploading ? (
        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
      ) : (
        <Upload className="h-4 w-4 mr-1" />
      )}
      {isUploading
        ? `上传中 ${(uploadProgress?.done ?? 0) + 1}/${uploadProgress?.total ?? 0} …`
        : "上传文档"}
    </Button>
  );

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={PERSONAL_DOC_ACCEPT}
        className="hidden"
        aria-label="选择个人文档文件"
        data-testid="personal-docs-file-input"
        onChange={handleFileSelect}
      />

      <div className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/5 p-3">
        <Lock className="h-4 w-4 mt-0.5 text-info shrink-0" />
        <div className="flex-1 space-y-1">
          <p className="text-xs text-foreground/80 leading-relaxed">
            个人文档仅自己可见,上传后即可在{persona.title}对话中使用;共享到租户库后默认给本部门同事看,需审核
          </p>
          {/* 0907 拍板「引导不设卡」:说破最大的坑(扫描件),并把高命中的问答模板摆到手边 */}
          <p className="text-2xs text-muted-foreground leading-relaxed">
            文字版文档效果最好;扫描件 / 图片型 PDF
            识别不出文字。问答形式(一问一答)的内容,{persona.title}引用最准
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {PERSONAL_DOC_TEMPLATES.map((tpl) => (
              <Button
                key={tpl.filename}
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-2xs text-info hover:text-info"
                onClick={() => downloadPersonalDocTemplate(tpl)}
              >
                <FileDown className="mr-1 h-3 w-3" />
                {tpl.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge
            variant={atCap ? "destructive" : countWarning ? "secondary" : "outline"}
            className="text-2xs"
          >
            {documents.length}/{PERSONAL_DOC_MAX_COUNT} 份
          </Badge>
          {countWarning && <p className="text-xs text-muted-foreground">{countWarning}</p>}
        </div>
        {documents.length > 0 && uploadButton}
      </div>

      {isLoading && (
        <div
          role="status"
          className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          加载中...
        </div>
      )}

      {!isLoading && isError && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <p className="text-sm text-destructive">加载失败,请重试</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            重试
          </Button>
        </div>
      )}

      {!isLoading && !isError && documents.length === 0 && (
        <EmptyState
          title="还没有个人文档"
          description={`上传一份 pdf / word / txt / markdown,${persona.title}对话就能引用它了。`}
          action={uploadButton}
        />
      )}

      {!isLoading && !isError && documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => {
            const zeroChunks = doc.status === "done" && (doc.chunkCount ?? 0) === 0;
            const statusType = zeroChunks ? "warning" : (STATUS_TYPE[doc.status] ?? "warning");
            const statusLabel = zeroChunks ? "无内容" : (STATUS_LABEL[doc.status] ?? doc.status);
            return (
              <Card key={doc.id} className="shadow-sm" data-testid="personal-doc-card">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{doc.filename}</p>
                      <p className="text-2xs text-muted-foreground">
                        {doc.createdAt
                          ? new Date(doc.createdAt).toLocaleString("zh-CN", { hour12: false })
                          : "-"}
                        {/* 解析质量透出(0907):段数是「军师引不引得到」的直接判据,别让用户猜 */}
                        {doc.status === "done" && (doc.chunkCount ?? 0) > 0 && (
                          <span> · 解析出 {doc.chunkCount} 段</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge type={statusType}>{statusLabel}</StatusBadge>
                    <Badge variant="outline" className="gap-1 text-2xs">
                      <Lock className="h-3 w-3" />
                      仅自己可见
                    </Badge>
                    {/* 2026-09-16:个人档下载走**专用端点** —— 共享库那个口按对内可见范围判权,
                        个人档在它眼里是 OWNER 档、一律回 404(那正是同期堵掉的越权洞)。 */}
                    {doc.originalAvailable && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={downloadMutation.isPending}
                        aria-label="下载原文件"
                        onClick={() =>
                          downloadMutation.mutate({ id: doc.id, filename: doc.filename })
                        }
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      disabled={promoteMutation.isPending}
                      onClick={() => promoteMutation.mutate(doc.id)}
                    >
                      <Share2 className="mr-1 h-3.5 w-3.5" />
                      共享到租户库
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={deleteMutation.isPending}
                      aria-label="删除文档"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `删除个人文档「${doc.filename}」?`,
                          description: "删除后不可恢复。",
                          confirmText: "删除",
                          variant: "destructive",
                        });
                        if (ok) deleteMutation.mutate(doc.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* 完成但 0 段 = 抽不出文字(典型:扫描件)——「可用」是假象,军师引用不到,直说并给出路 */}
                  {doc.status === "done" && (doc.chunkCount ?? 0) === 0 && (
                    <p className="flex w-full items-start gap-1.5 text-2xs leading-snug text-warning">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      没解析出文字(可能是扫描件或图片型
                      PDF),{persona.title}引用不到这份文档——建议转成文字版后删除重传
                    </p>
                  )}
                  {/* 0816 反馈同款口径;0926:只画人话(码 → 人话,存量原文/未知 → 兜底句)。 */}
                  {kbFailureText(doc) && (
                    <p className="w-full text-2xs leading-snug text-destructive">
                      {kbFailureText(doc)}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
